import { Fragment, type ReactNode } from 'react';

/** Minimale Markdown: ## / ###, **bold**, `- ` lijsten, lege regels. Geen HTML van gebruikers doorlaten. */
export function SimpleMarkdown({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  function inline(s: string) {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, j) => {
      if (p.startsWith('**') && p.endsWith('**')) {
        return (
          <strong key={j} className="font-semibold text-ct-text">
            {p.slice(2, -2)}
          </strong>
        );
      }
      return <Fragment key={j}>{p}</Fragment>;
    });
  }

  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('## ')) {
      blocks.push(
        <h2 key={key++} className="mt-6 text-xl font-bold text-ct-text first:mt-0">
          {inline(line.slice(3).trim())}
        </h2>
      );
      i++;
      continue;
    }
    if (line.startsWith('### ')) {
      blocks.push(
        <h3 key={key++} className="mt-4 text-lg font-semibold text-ct-text">
          {inline(line.slice(4).trim())}
        </h3>
      );
      i++;
      continue;
    }
    if (line.trim().startsWith('- ')) {
      const items: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      blocks.push(
        <ul key={key++} className="mt-2 list-inside list-disc space-y-1 text-ct-muted">
          {items.map((item, idx) => (
            <li key={idx}>{inline(item)}</li>
          ))}
        </ul>
      );
      continue;
    }
    if (line.trim().startsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      const parseRow = (row: string) =>
        row
          .split('|')
          .map((c) => c.trim())
          .filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);

      const isSeparator = (row: string) => /^[\|\-\s:]+$/.test(row);
      const dataLines = tableLines.filter((l) => !isSeparator(l));
      const [headerRow, ...bodyRows] = dataLines;
      const headers = parseRow(headerRow ?? '');
      blocks.push(
        <div key={key++} className="mt-4 overflow-x-auto rounded-lg border border-ct-border">
          <table className="w-full text-sm text-left">
            <thead className="bg-ct-surface border-b border-ct-border">
              <tr>
                {headers.map((h, idx) => (
                  <th key={idx} className="px-3 py-2 font-semibold text-ct-text whitespace-nowrap">
                    {inline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, rIdx) => (
                <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white dark:bg-transparent' : 'bg-ct-surface/50'}>
                  {parseRow(row).map((cell, cIdx) => (
                    <td key={cIdx} className="px-3 py-2 text-ct-muted border-t border-ct-border/50">
                      {inline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    if (line.trim() === '') {
      i++;
      continue;
    }
    const para: string[] = [line];
    i++;
    while (i < lines.length && lines[i].trim() !== '' && !/^#{2,3}\s/.test(lines[i])) {
      if (lines[i].trim().startsWith('- ')) break;
      if (lines[i].trim().startsWith('|')) break;
      para.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={key++} className="mt-2 leading-relaxed text-ct-muted">
        {inline(para.join(' '))}
      </p>
    );
  }

  return <div className="space-y-1 text-sm">{blocks}</div>;
}
